/**
 * THE ROCKET — fire along your heading, break apart the first thing it reaches.
 *
 * The offensive half of the owner's list: "a rocket to shoot out at another circle which will
 * split it into several smaller circles". It is the reach a split cannot buy you — a split puts
 * your own body out in front of you at the price of being in pieces, and this puts *somebody
 * else* in pieces at the price of the orb you were carrying.
 *
 * It reuses the spike's shatter (`RoomSim.shatterCell` -> `burstCell`), which is the whole reason
 * this file is nothing but numbers: what a shatter does, how many pieces it makes, how they are
 * laid out and how MAX_CELLS_PER_PLAYER bounds it is decided in one place, and a second copy of
 * that arithmetic would be a second thing to keep in step with the renderer's outline.
 *
 * IT BREAKS THE CELL IT HIT, not the cluster. That is the spike's scope and it is deliberate:
 * MAX_CELLS_PER_PLAYER is a budget the *cluster* shares, so shattering every cell of a player in
 * eight pieces would be arithmetic the cap refuses most of, and the pieces it did make would be
 * the crumbs. For the overwhelmingly common single-cell target the hit cell *is* the cluster,
 * which is the case the card is written about; against a player already in pieces a rocket
 * splinters the piece it caught, exactly as driving that piece into a spike would.
 */

import { PROJECTILE_MUZZLE_CLEARANCE, TICK_RATE_HZ } from '../constants/simulation.js';
import type { PowerUp } from './powerup.js';

export const POWER_UP_ROCKET = 'rocket';

/**
 * World units per second. Two and a half times BASE_SPEED, because a rocket that a starting
 * player could outrun is a rocket nobody would ever be hit by — and because being *able* to dodge
 * one by turning is the skill the speed is tuned around, not being able to outpace it in a
 * straight line.
 */
export const ROCKET_SPEED = 900;

/**
 * How far it may travel, in world units — a fifth of the map, and the answer to "must not be
 * usable to grief across the whole map".
 *
 * It is a little under the widest interest region a viewer is replicated (see `constants/view`),
 * so a rocket cannot reach somebody who was never drawn on the firer's screen. Firing at a blob
 * you can see is the whole of the aiming problem; firing at coordinates is not a mechanic.
 */
export const ROCKET_RANGE = 1_200;

/** Rocket-sized rather than pellet-sized: it has to be legible in flight at any zoom. */
export const ROCKET_RADIUS = 18;

/**
 * The second bound, in ticks, and it is derived from the first so the two can never disagree:
 * a rocket that ran out of ticks before it ran out of range would have a range nobody wrote down.
 */
export const ROCKET_LIFETIME_TICKS = Math.ceil((ROCKET_RANGE / ROCKET_SPEED) * TICK_RATE_HZ);

/**
 * How long the window runs, in seconds. **Ten**, and it is the player's own number: "the missles
 * should be unlimited for 10 seconds (shoot as much as you can)... it's kind of a chaos inducing
 * one which would be fun since it's timed and you're just firing as much as you can until
 * finished." The rocket is therefore the registry's model case for `timed` — a window that is
 * burning whether or not the button is pressed, so the only way to be paid for it is to spend it.
 */
export const ROCKET_DURATION_SECONDS = 10;
export const ROCKET_DURATION_TICKS = ROCKET_DURATION_SECONDS * TICK_RATE_HZ;

/**
 * The gap between two rockets, in ticks. **Eight — 0.4 s, about twenty-five rockets a window.**
 *
 * This is the number "unlimited" actually means, and it is derived rather than chosen: a rocket
 * lives ROCKET_LIFETIME_TICKS (~27) and this is a little under a third of that, so a firer holding
 * the button keeps three or four of their own in the air at once — a stream a target can read as a
 * stream rather than as a wall, and far enough under MAX_PROJECTILES_PER_ROOM that one player on a
 * spree cannot close the room's budget to everybody else. Without it MAX_INPUTS_PER_TICK would set
 * the rate at twenty a second, which is two hundred rockets out of one orb.
 */
export const ROCKET_FIRE_COOLDOWN_TICKS = 8;

export const rocketPowerUp: PowerUp = {
  id: POWER_UP_ROCKET,
  name: 'Rocket',
  description: 'Fires along your heading and shatters the first player it hits.',
  glyph: '🚀',
  use: {
    model: 'timed',
    durationTicks: ROCKET_DURATION_TICKS,
    cooldownTicks: ROCKET_FIRE_COOLDOWN_TICKS,
  },
  projectile: {
    motion: 'launched',
    effect: 'shatter',
    speed: ROCKET_SPEED,
    range: ROCKET_RANGE,
    lifetimeTicks: ROCKET_LIFETIME_TICKS,
    // Live from the tick it leaves: it is launched from outside the firer's own rim and cannot
    // hit them, so there is nothing an arming delay would protect anybody from.
    armingTicks: 0,
    radius: ROCKET_RADIUS,
    standoff: PROJECTILE_MUZZLE_CLEARANCE,
    hitsOwner: false,
  },
};
